/**
 * MultiChoiceFieldConfigPanel Component
 *
 * Configuration panel for MultiChoice field type.
 * Matches the existing UI-Model pattern with Design System components.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MultiChoiceFieldConfigPanelProps } from '../Fields/types';
import {
  MULTICHOICE_DEFAULT_CONFIG,
  MultiChoiceFieldConfig,
  MultiChoiceStyle,
  ChipSize,
  MultiChoiceImageDisplay,
  LayoutStyle,
  MultiChoiceTextAlignment,
  ColumnSelection,
  LayoutDirection,
  ChipWidth,
  ChipLabelPosition,
  LoadOption,
  LoadRemainingOption,
  ExcludedOption,
  InfoDisplay,
  InfoTrigger,
  InfoSize,
  InfoForOptionsType,
} from '../../config/fieldDefaults';
import Toggle from 'Components/DesignSystem/Toggle/Toggle';
import TextField from 'Components/DesignSystem/TextField/TextField';
import FieldTitle from 'Components/DesignSystem/FieldTitle/FieldTitle';
import Typography from 'Components/DesignSystem/Typography/Typography';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import Radio from 'Components/DesignSystem/Radio/Radio';
import { useDebouncedInput } from '../../hooks/useDebouncedInput';
import CollapsibleSection from './CollapsibleSection';

const DEBOUNCE_DELAY = 500;

// Dropdown Options
const STYLE_OPTIONS = [
  { value: 'Chip', label: 'Chip' },
  { value: 'Checkbox', label: 'Checkbox' },
  { value: 'Radio Button', label: 'Radio Button' },
  { value: 'Dropdown', label: 'Dropdown' },
];

const CHIP_SIZE_OPTIONS = [
  { value: 'Medium', label: 'Medium' },
  { value: 'Large', label: 'Large' },
];

const IMAGE_DISPLAY_OPTIONS = [
  { value: 'Fit', label: 'Fit' },
  { value: 'Fill', label: 'Fill' },
];

const LAYOUT_STYLE_OPTIONS = [
  { value: 'Standard', label: 'Standard' },
  { value: 'Padded Frame', label: 'Padded Frame' },
];

const COLUMN_OPTIONS = [
  { value: 'Auto', label: 'Auto' },
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
];

const INFO_DISPLAY_OPTIONS = [
  { value: 'Inside', label: 'Inside' },
  { value: 'Outside', label: 'Outside' },
];

const INFO_TRIGGER_OPTIONS = [
  { value: 'Hover', label: 'Hover' },
  { value: 'Click', label: 'Click' },
];

const INFO_SIZE_OPTIONS = [
  { value: 'Auto', label: 'Auto' },
  { value: 'Small', label: 'Small' },
  { value: 'Medium', label: 'Medium' },
  { value: 'Large', label: 'Large' },
];

const EXCLUDED_OPTIONS = [
  { value: 'Hide', label: 'Hide' },
  { value: 'Disable', label: 'Disable' },
  { value: 'Disable and moved to the bottom of the list', label: 'Disable and move to bottom' },
];

export const MultiChoiceFieldConfigPanel = ({
  config,
  onChange,
}: MultiChoiceFieldConfigPanelProps) => {
  const [localConfig, setLocalConfig] = useState<MultiChoiceFieldConfig>(() => {
    const defaults = {
      label: '',
      displayName: '',
      ...MULTICHOICE_DEFAULT_CONFIG,
      // Override defaults to match existing UI-Model pattern
      initialLoadCount: 5,
      loadMoreButtonLabel: 'Show More',
      chipWidth: 'Dynamic' as ChipWidth,
    };
    return { ...defaults, ...config };
  });

  useEffect(() => {
    const defaults = {
      label: '',
      displayName: '',
      ...MULTICHOICE_DEFAULT_CONFIG,
      initialLoadCount: 5,
      loadMoreButtonLabel: 'Show More',
      chipWidth: 'Dynamic' as ChipWidth,
    };
    setLocalConfig({ ...defaults, ...config });
  }, [config]);

  const handleConfigChange = useCallback(
    <K extends keyof MultiChoiceFieldConfig>(key: K, value: MultiChoiceFieldConfig[K]) => {
      const newConfig = { ...localConfig, [key]: value };
      setLocalConfig(newConfig);
      onChange(newConfig);
    },
    [localConfig, onChange]
  );

  // Initial Load Count - local string state with editing guard (bug #909 pattern)
  const isEditingInitialLoadCountRef = useRef<boolean>(false);
  const [localInitialLoadCount, setLocalInitialLoadCount] = useState<string>(
    (localConfig.initialLoadCount || 5).toString()
  );
  const initialLoadCountDebounceRef = useRef<NodeJS.Timeout>(null);

  // Batch Count - same pattern
  const isEditingBatchCountRef = useRef<boolean>(false);
  const [localBatchCount, setLocalBatchCount] = useState<string>(
    (localConfig.batchCount || 5).toString()
  );
  const batchCountDebounceRef = useRef<NodeJS.Timeout>(null);

  useEffect(() => {
    if (!isEditingInitialLoadCountRef.current) {
      setLocalInitialLoadCount((localConfig.initialLoadCount || 5).toString());
    }
  }, [localConfig.initialLoadCount]);

  useEffect(() => {
    if (!isEditingBatchCountRef.current) {
      setLocalBatchCount((localConfig.batchCount || 5).toString());
    }
  }, [localConfig.batchCount]);

  useEffect(() => {
    return () => {
      if (initialLoadCountDebounceRef.current) clearTimeout(initialLoadCountDebounceRef.current);
      if (batchCountDebounceRef.current) clearTimeout(batchCountDebounceRef.current);
    };
  }, []);

  const displayNameInput = useDebouncedInput(
    localConfig.displayName,
    (value) => handleConfigChange('displayName', value),
    DEBOUNCE_DELAY
  );

  const shortDisplayNameInput = useDebouncedInput(
    localConfig.shortDisplayName,
    (value) => handleConfigChange('shortDisplayName', value),
    DEBOUNCE_DELAY
  );

  const loadMoreLabelInput = useDebouncedInput(
    localConfig.loadMoreButtonLabel,
    (value) => handleConfigChange('loadMoreButtonLabel', value),
    DEBOUNCE_DELAY
  );

  const loadLessLabelInput = useDebouncedInput(
    localConfig.loadLessButtonLabel,
    (value) => handleConfigChange('loadLessButtonLabel', value),
    DEBOUNCE_DELAY
  );

  // Check if current style supports certain options
  const isChipStyle = localConfig.style === 'Chip';
  const isDropdownStyle = localConfig.style === 'Dropdown';
  const isLargeChip = isChipStyle && localConfig.chipSize === 'Large';
  const isMediumChip = isChipStyle && localConfig.chipSize === 'Medium';
  const showLayoutDirection = !isDropdownStyle;

  return (
    <div className="tw-space-y-4">
      <div className="tw-p-1">
        {/* Label Section - Read-only (matches existing UI-Model pattern) */}
        <div className="tw-mb-6">
          <TextField
            label="Label"
            labelSize="sm"
            value={localConfig.label}
            disabled
            placeholder="Field name"
          />
        </div>

        {/* Style Type Section (matches existing UI-Model pattern) */}
        <div className="tw-mb-6">
          <FieldTitle label="Style Type" size="sm" className="tw-mb-2" />
          <Typography variant="body-primary" className="tw-text-ink">
            MultiChoice Field
          </Typography>
        </div>

        {/* Show Title Section with conditional Display Name & Short Display Name */}
        <div className="tw-mb-4">
          <Checkbox
            label="Show Title"
            checked={localConfig.showTitle}
            onChange={(checked) => handleConfigChange('showTitle', checked)}
          />

          {/* Display Name - shown when Show Title is checked */}
          {localConfig.showTitle && (
            <div className="tw-mt-3 tw-ml-8">
              <TextField
                label="Display Name"
                labelSize="sm"
                value={displayNameInput.localValue}
                onChange={displayNameInput.handleChange}
                placeholder="Enter display name"
              />
            </div>
          )}

          {/* Short Display Name - shown when Show Title is checked */}
          {localConfig.showTitle && (
            <div className="tw-mt-3 tw-ml-8">
              <TextField
                label="Short Display Name (Optional)"
                labelSize="sm"
                value={shortDisplayNameInput.localValue}
                onChange={shortDisplayNameInput.handleChange}
                placeholder="Enter Short Display Name"
                infoMessage="A concise name displayed in the build/configuration view instead of the full Display Name. Example: Use 'Finish' instead of 'Would you like to select a finish for your product?' If left empty, the Display Name will be used."
              />
            </div>
          )}
        </div>

        {/* Show Info for Field */}
        <div className="tw-mb-4">
          <Checkbox
            label="Show 'Info for Field'"
            checked={localConfig.showInfoForField}
            onChange={(checked) => handleConfigChange('showInfoForField', checked)}
          />
          {localConfig.showInfoForField && (
            <div className="tw-mt-3 tw-ml-8">
              <div className="tw-grid tw-grid-cols-2 tw-gap-4 tw-mb-4">
                <Dropdown
                  label="Trigger"
                  labelSize="sm"
                  value={localConfig.infoForFieldTrigger}
                  options={INFO_TRIGGER_OPTIONS}
                  onChange={(value) => handleConfigChange('infoForFieldTrigger', value as InfoTrigger)}
                  infoMessage="Hover: mouse hover over 'i' icon to view the info tooltip. Click: user must click the 'i' icon to view the info tooltip."
                />
                <div>
                  <FieldTitle
                    label="Auto Pin"
                    size="sm"
                    className="tw-mb-2"
                    infoMessage="When enabled, the info tooltip automatically pins and stays visible."
                  />
                  <Toggle
                    label=""
                    checked={localConfig.infoForFieldOpenInWindow}
                    onChange={(checked) => handleConfigChange('infoForFieldOpenInWindow', checked)}
                  />
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                <Dropdown
                  label="Size"
                  labelSize="sm"
                  value={localConfig.infoForFieldSize}
                  options={INFO_SIZE_OPTIONS}
                  onChange={(value) => handleConfigChange('infoForFieldSize', value as InfoSize)}
                  infoMessage="Auto: automatically fits the tooltip window to content. Small/Medium/Large: fixed size window, user can drag to resize."
                />
              </div>
            </div>
          )}
        </div>

        {/* Style Settings Section */}
        <CollapsibleSection title="Style Settings" defaultOpen>
          <div className="tw-space-y-4">
            {/* Style Dropdown */}
            <Dropdown
              label="Style"
              labelSize="sm"
              value={localConfig.style}
              options={STYLE_OPTIONS}
              onChange={(value) => handleConfigChange('style', value as MultiChoiceStyle)}
            />

            {/* Chip-specific options */}
            {isChipStyle && (
              <>
                {/* Chip Size */}
                <Dropdown
                  label="Chip Size"
                  labelSize="sm"
                  value={localConfig.chipSize}
                  options={CHIP_SIZE_OPTIONS}
                  onChange={(value) => handleConfigChange('chipSize', value as ChipSize)}
                />

                {/* Show Image - Only for Chip style */}
                <Checkbox
                  label="Show Image"
                  checked={localConfig.showImage}
                  onChange={(checked) => handleConfigChange('showImage', checked)}
                />

                {/* Image Display - Only when showImage is true */}
                {localConfig.showImage && (
                  <div className="tw-ml-8">
                    <Dropdown
                      label="Image Display"
                      labelSize="sm"
                      value={localConfig.imageDisplay}
                      options={IMAGE_DISPLAY_OPTIONS}
                      onChange={(value) => handleConfigChange('imageDisplay', value as MultiChoiceImageDisplay)}
                      infoMessage="How the image should be displayed within the chip"
                    />
                  </div>
                )}

                {/* Large Chip + showImage specific options */}
                {isLargeChip && localConfig.showImage && (
                  <>
                    {/* Layout Style */}
                    <div className="tw-ml-8">
                      <Dropdown
                        label="Layout Style"
                        labelSize="sm"
                        value={localConfig.layoutStyle}
                        options={LAYOUT_STYLE_OPTIONS}
                        onChange={(value) => handleConfigChange('layoutStyle', value as LayoutStyle)}
                        infoMessage="Visual layout style for the chip"
                      />
                    </div>

                    {/* Chip Label Position - Large Chip */}
                    <div className="tw-ml-8">
                      <FieldTitle label="Chip Label Position" size="sm" className="tw-mb-2" />
                      <div className="tw-flex tw-flex-col tw-gap-2">
                        <Radio
                          label="Right side of image"
                          value="Right side of image"
                          checked={localConfig.chipLabelPosition === 'Right side of image'}
                          onChange={() => handleConfigChange('chipLabelPosition', 'Right side of image')}
                          name="chipLabelPositionLarge"
                        />
                        <Radio
                          label="Below Image"
                          value="Below Image"
                          checked={localConfig.chipLabelPosition === 'Below Image'}
                          onChange={() => handleConfigChange('chipLabelPosition', 'Below Image')}
                          name="chipLabelPositionLarge"
                        />
                        <Radio
                          label="Show only on hover"
                          value="Show only on hover"
                          checked={localConfig.chipLabelPosition === 'Show only on hover'}
                          onChange={() => handleConfigChange('chipLabelPosition', 'Show only on hover')}
                          name="chipLabelPositionLarge"
                        />
                        <Radio
                          label="Hide label"
                          value="Hide label"
                          checked={localConfig.chipLabelPosition === 'Hide label'}
                          onChange={() => handleConfigChange('chipLabelPosition', 'Hide label')}
                          name="chipLabelPositionLarge"
                        />
                      </div>
                    </div>

                    {/* Caption Alignment - Large Chip with images */}
                    <div className="tw-ml-8">
                      <FieldTitle label="Chip Label Alignment" size="sm" className="tw-mb-2" />
                      <div className="tw-flex tw-items-center tw-gap-4">
                        <Radio
                          label="Left"
                          value="Left"
                          checked={localConfig.captionAlignment === 'Left'}
                          onChange={() => handleConfigChange('captionAlignment', 'Left')}
                          name="captionAlignment"
                        />
                        <Radio
                          label="Centre"
                          value="Centre"
                          checked={localConfig.captionAlignment === 'Centre'}
                          onChange={() => handleConfigChange('captionAlignment', 'Centre')}
                          name="captionAlignment"
                        />
                        <Radio
                          label="Right"
                          value="Right"
                          checked={localConfig.captionAlignment === 'Right'}
                          onChange={() => handleConfigChange('captionAlignment', 'Right')}
                          name="captionAlignment"
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Chip Label Position - Medium Chip with images */}
                {isMediumChip && localConfig.showImage && (
                  <div className="tw-ml-8">
                    <FieldTitle label="Chip Label Position" size="sm" className="tw-mb-2" />
                    <div className="tw-flex tw-flex-col tw-gap-2">
                      <Radio
                        label="Right side of image"
                        value="Right side of image"
                        checked={localConfig.chipLabelPosition === 'Right side of image'}
                        onChange={() => handleConfigChange('chipLabelPosition', 'Right side of image')}
                        name="chipLabelPositionMedium"
                      />
                      <Radio
                        label="Below Image"
                        value="Below Image"
                        checked={localConfig.chipLabelPosition === 'Below Image'}
                        onChange={() => handleConfigChange('chipLabelPosition', 'Below Image')}
                        name="chipLabelPositionMedium"
                      />
                      <Radio
                        label="Show only on hover"
                        value="Show only on hover"
                        checked={localConfig.chipLabelPosition === 'Show only on hover'}
                        onChange={() => handleConfigChange('chipLabelPosition', 'Show only on hover')}
                        name="chipLabelPositionMedium"
                      />
                      <Radio
                        label="Hide label"
                        value="Hide label"
                        checked={localConfig.chipLabelPosition === 'Hide label'}
                        onChange={() => handleConfigChange('chipLabelPosition', 'Hide label')}
                        name="chipLabelPositionMedium"
                      />
                    </div>
                  </div>
                )}

                {/* Chip Width */}
                <div>
                  <FieldTitle label="Chip Width" size="sm" className="tw-mb-2" />
                  <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-2 sm:tw-gap-4">
                    <Radio
                      label="Fixed"
                      value="Fixed"
                      checked={localConfig.chipWidth === 'Fixed'}
                      onChange={() => handleConfigChange('chipWidth', 'Fixed')}
                      name="chipWidth"
                      disabled
                    />
                    <Radio
                      label="Dynamic"
                      value="Dynamic"
                      checked={localConfig.chipWidth === 'Dynamic'}
                      onChange={() => handleConfigChange('chipWidth', 'Dynamic')}
                      name="chipWidth"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Checkbox/Radio Button specific options - Show Image */}
            {(localConfig.style === 'Checkbox' || localConfig.style === 'Radio Button') && (
              <>
                <Checkbox
                  label="Show Image"
                  checked={localConfig.showImage}
                  onChange={(checked) => handleConfigChange('showImage', checked)}
                />

                {localConfig.showImage && (
                  <div className="tw-ml-8">
                    <Dropdown
                      label="Image Display"
                      labelSize="sm"
                      value={localConfig.imageDisplay}
                      options={IMAGE_DISPLAY_OPTIONS}
                      onChange={(value) => handleConfigChange('imageDisplay', value as MultiChoiceImageDisplay)}
                      infoMessage="Choose how the image should be displayed"
                    />
                  </div>
                )}
              </>
            )}

            {/* Layout Direction - Not shown for Dropdown */}
            {showLayoutDirection && (
              <div>
                <FieldTitle label="Layout" size="sm" className="tw-mb-2" />
                <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-2 sm:tw-gap-4">
                  <Radio
                    label="Horizontal"
                    value="Horizontal"
                    checked={localConfig.layoutDirection === 'Horizontal'}
                    onChange={() => handleConfigChange('layoutDirection', 'Horizontal')}
                    name="layoutDirection"
                  />
                  <Radio
                    label="Vertical"
                    value="Vertical"
                    checked={localConfig.layoutDirection === 'Vertical'}
                    onChange={() => handleConfigChange('layoutDirection', 'Vertical')}
                    name="layoutDirection"
                  />
                </div>
              </div>
            )}

            {/* Show Search Bar - Only for non-Dropdown styles */}
            {showLayoutDirection && (
              <Checkbox
                label="Show 'Search Bar'"
                checked={localConfig.showSearchBar}
                onChange={(checked) => handleConfigChange('showSearchBar', checked)}
              />
            )}

            {/* Show Expand/Collapse - Only for non-Dropdown styles */}
            {showLayoutDirection && (
              <div>
                <Checkbox
                  label="Show Expand/Collapse"
                  checked={localConfig.showExpandCollapse}
                  onChange={(checked) => {
                    const newConfig = {
                      ...localConfig,
                      showExpandCollapse: checked,
                      collapseByDefault: checked ? localConfig.collapseByDefault : false,
                    };
                    setLocalConfig(newConfig);
                    onChange(newConfig);
                  }}
                />
                {localConfig.showExpandCollapse && (
                  <div className="tw-mt-2 tw-ml-8">
                    <Checkbox
                      label="Collapse by Default"
                      checked={localConfig.collapseByDefault}
                      onChange={(checked) => handleConfigChange('collapseByDefault', checked)}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </CollapsibleSection>

        {/* Load Options Section */}
        <CollapsibleSection title="Load Options">
          <div className="tw-space-y-4">
            {/* Load Option */}
            <div>
              <FieldTitle label="Load Options" size="sm" className="tw-mb-2" />
              <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-2 sm:tw-gap-4">
                <Radio
                  label="All"
                  value="All"
                  checked={localConfig.loadOption === 'All'}
                  onChange={() => handleConfigChange('loadOption', 'All')}
                  name="loadOption"
                />
                <Radio
                  label="Partial"
                  value="Partial"
                  checked={localConfig.loadOption === 'Partial'}
                  onChange={() => handleConfigChange('loadOption', 'Partial')}
                  name="loadOption"
                />
              </div>
            </div>

            {/* Partial Load Options */}
            {localConfig.loadOption === 'Partial' && (
              <div className="tw-ml-8 tw-space-y-4">
                {/* Button Label */}
                <TextField
                  label="Button Label"
                  labelSize="sm"
                  value={loadMoreLabelInput.localValue}
                  onChange={loadMoreLabelInput.handleChange}
                  placeholder="Enter button label"
                />

                {/* Show Less Button Label */}
                <TextField
                  label="Show Less Button Label"
                  labelSize="sm"
                  value={loadLessLabelInput.localValue}
                  onChange={loadLessLabelInput.handleChange}
                  placeholder="Enter button label"
                />

                {/* Initial Load Count */}
                <TextField
                  label="Initial Load Count"
                  labelSize="sm"
                  value={localInitialLoadCount}

                  onChange={(value) => {
                    isEditingInitialLoadCountRef.current = true;
                    const inputValue = String(value);
                    if (inputValue === '') { setLocalInitialLoadCount(''); return; }
                    if (!/^\d+$/.test(inputValue)) return;
                    setLocalInitialLoadCount(inputValue);
                    const num = parseInt(inputValue, 10);
                    if (num >= 1) {
                      if (initialLoadCountDebounceRef.current) clearTimeout(initialLoadCountDebounceRef.current);
                      initialLoadCountDebounceRef.current = setTimeout(() => handleConfigChange('initialLoadCount', num), DEBOUNCE_DELAY);
                    }
                  }}
                  onBlur={() => {
                    isEditingInitialLoadCountRef.current = false;
                    if (initialLoadCountDebounceRef.current) { clearTimeout(initialLoadCountDebounceRef.current); initialLoadCountDebounceRef.current = null; }
                    const v = localInitialLoadCount;
                    if (v === '' || !/^\d+$/.test(v) || parseInt(v, 10) < 1) {
                      setLocalInitialLoadCount('5');
                      handleConfigChange('initialLoadCount', 5);
                    } else {
                      const num = parseInt(v, 10);
                      setLocalInitialLoadCount(num.toString());
                      handleConfigChange('initialLoadCount', num);
                    }
                  }}
                  placeholder="Enter count"
                  infoMessage="Number of items to show initially"
                />

                {/* Load Remaining Count */}
                <div>
                  <FieldTitle
                    label="Load Remaining Count"
                    size="sm"
                    className="tw-mb-2"
                    infoMessage="How to load the remaining items"
                  />
                  <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-2 sm:tw-gap-4">
                    <Radio
                      label="Show All"
                      value="Show All"
                      checked={localConfig.loadRemainingOption === 'Show All'}
                      onChange={() => handleConfigChange('loadRemainingOption', 'Show All')}
                      name="loadRemainingOption"
                    />
                    <Radio
                      label="In Batch"
                      value="In Batch"
                      checked={localConfig.loadRemainingOption === 'In Batch'}
                      onChange={() => handleConfigChange('loadRemainingOption', 'In Batch')}
                      name="loadRemainingOption"
                    />
                  </div>
                </div>

                {/* Batch Count - Only show when In Batch is selected */}
                {localConfig.loadRemainingOption === 'In Batch' && (
                  <TextField
                    label="Batch Count"
                    labelSize="sm"
                    value={localBatchCount}

                    onChange={(value) => {
                      isEditingBatchCountRef.current = true;
                      const inputValue = String(value);
                      if (inputValue === '') { setLocalBatchCount(''); return; }
                      if (!/^\d+$/.test(inputValue)) return;
                      setLocalBatchCount(inputValue);
                      const num = parseInt(inputValue, 10);
                      if (num >= 1) {
                        if (batchCountDebounceRef.current) clearTimeout(batchCountDebounceRef.current);
                        batchCountDebounceRef.current = setTimeout(() => handleConfigChange('batchCount', num), DEBOUNCE_DELAY);
                      }
                    }}
                    onBlur={() => {
                      isEditingBatchCountRef.current = false;
                      if (batchCountDebounceRef.current) { clearTimeout(batchCountDebounceRef.current); batchCountDebounceRef.current = null; }
                      const v = localBatchCount;
                      if (v === '' || !/^\d+$/.test(v) || parseInt(v, 10) < 1) {
                        setLocalBatchCount('5');
                        handleConfigChange('batchCount', 5);
                      } else {
                        const num = parseInt(v, 10);
                        setLocalBatchCount(num.toString());
                        handleConfigChange('batchCount', num);
                      }
                    }}
                    placeholder="Enter batch count"
                    infoMessage="Number of items to load per batch"
                  />
                )}
              </div>
            )}
          </div>
        </CollapsibleSection>

        {/* Exclusions Section - combines Field and Option exclusions */}
        <CollapsibleSection title="Exclusions">
          <div className="tw-space-y-4">
            {/* Field Exclusions */}
            <div>
              <FieldTitle label="Field exclusions" size="sm" className="tw-mb-2" />
              <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-2 sm:tw-gap-4">
                <Radio
                  label="Hide"
                  value="hide"
                  checked={localConfig.exclusionType === 'hide'}
                  onChange={() => handleConfigChange('exclusionType', 'hide')}
                  name="fieldExclusion"
                />
                <Radio
                  label="Disable"
                  value="disable"
                  checked={localConfig.exclusionType === 'disable'}
                  onChange={() => handleConfigChange('exclusionType', 'disable')}
                  name="fieldExclusion"
                />
              </div>
            </div>

            {/* Option Exclusions */}
            <Dropdown
              label="Option exclusions"
              labelSize="sm"
              value={localConfig.excludedOption}
              options={EXCLUDED_OPTIONS}
              onChange={(value) => handleConfigChange('excludedOption', value as ExcludedOption)}
              infoMessage="How to display options that are excluded by rules"
            />

            {/* Hide field when no options are available */}
            <Checkbox
              label="Hide field when no options are available"
              checked={localConfig.hideWhenNoOptions}
              onChange={(checked) => handleConfigChange('hideWhenNoOptions', checked)}
            />
          </div>
        </CollapsibleSection>

        {/* Show Info for Options Section */}
        <CollapsibleSection title="Show Info for Options">
          <div className="tw-space-y-4">
            <Checkbox
              label="Show 'Info for Options'"
              checked={localConfig.showInfoForOptions}
              onChange={(checked) => handleConfigChange('showInfoForOptions', checked)}
            />
            {localConfig.showInfoForOptions && (
              <div className="tw-ml-8 tw-space-y-4">
                {/* Type: Icon or Inline */}
                <div>
                  <FieldTitle label="Type" size="sm" className="tw-mb-2" />
                  <div className="tw-flex tw-gap-4">
                    <Radio
                      label="Icon"
                      value="Icon"
                      checked={localConfig.infoForOptionsType === 'Icon'}
                      onChange={() => handleConfigChange('infoForOptionsType', 'Icon')}
                      name="infoForOptionsType"
                    />
                    <Radio
                      label="Inline"
                      value="Inline"
                      checked={localConfig.infoForOptionsType === 'Inline'}
                      onChange={() => handleConfigChange('infoForOptionsType', 'Inline')}
                      name="infoForOptionsType"
                    />
                  </div>
                </div>
                {/* 2x2 grid: Display, Trigger, Size, Auto Pin */}
                <div className="tw-grid tw-grid-cols-2 tw-gap-4 tw-mb-4">
                  <Dropdown
                    label="Display"
                    labelSize="sm"
                    value={localConfig.infoForOptionsDisplay}
                    options={INFO_DISPLAY_OPTIONS}
                    onChange={(value) => handleConfigChange('infoForOptionsDisplay', value as InfoDisplay)}
                    infoMessage="Inside: places the 'i' icon within the chip. Outside: places the 'i' icon on the top right of the chip."
                  />
                  <Dropdown
                    label="Trigger"
                    labelSize="sm"
                    value={localConfig.infoForOptionsTrigger}
                    options={INFO_TRIGGER_OPTIONS}
                    onChange={(value) => handleConfigChange('infoForOptionsTrigger', value as InfoTrigger)}
                    infoMessage="Hover: mouse hover over 'i' icon to view the info tooltip. Click: user must click the 'i' icon to view the info tooltip."
                  />
                </div>
                <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                  <Dropdown
                    label="Size"
                    labelSize="sm"
                    value={localConfig.infoForOptionsSize}
                    options={INFO_SIZE_OPTIONS}
                    onChange={(value) => handleConfigChange('infoForOptionsSize', value as InfoSize)}
                    infoMessage="Auto: automatically fits the tooltip window to content. Small/Medium/Large: fixed size window, user can drag to resize."
                  />
                  <div>
                    <FieldTitle
                      label="Auto Pin"
                      size="sm"
                      className="tw-mb-2"
                      infoMessage="When enabled, the info tooltip automatically pins and stays visible."
                    />
                    <Toggle
                      label=""
                      checked={localConfig.infoForOptionsOpenInWindow}
                      onChange={(checked) => handleConfigChange('infoForOptionsOpenInWindow', checked)}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </CollapsibleSection>

        {/* Advanced Section - Commented out until pattern is finalized
        <CollapsibleSection title="Advanced">
          <div className="tw-space-y-4">
            <Checkbox
              label="Variant Defining Attribute (VDA)"
              checked={localConfig.vda}
              onChange={(checked) => handleConfigChange('vda', checked)}
            />
            <Typography variant="caption-metadata" className="tw-text-ink-tertiary tw-ml-8">
              Mark this field as a variant defining attribute
            </Typography>
          </div>
        </CollapsibleSection>
        */}
      </div>
    </div>
  );
};

export default MultiChoiceFieldConfigPanel;
